import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import {
  DollarSign, TrendingUp, Percent, Wallet, Plus, Pencil, Trash2, Loader2,
  X, Star, LayoutList, Receipt, Eye, EyeOff, Car, Clock, MessageSquare, Images, Sparkles, Zap,
} from "lucide-react";
import CarProviderPanel from "@/components/CarProviderPanel";
import TripChat from "@/components/TripChat";
import MultiImageUpload from "@/components/MultiImageUpload";

const ISLANDS = ["Grenada", "St. Barth", "Barbados", "Jamaica", "Saint Lucia", "Turks & Caicos", "Bahamas"];
const CATEGORIES = ["Rides", "Tours", "Activities", "Transfers"];
const EMPTY = {
  title: "", description: "", island: "Grenada", category: "Tours",
  price: 80, price_type: "per_person", duration: "", meeting_point: "",
  max_guests: 8, location: "", images: [""], amenities: "", is_available: true, cancellation_window: 24,
  affiliate_enabled: false, affiliate_bonus_enabled: false, affiliate_bonus_rate: 5,
};

export default function ProviderDashboard() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState("overview");
  const [earnings, setEarnings] = useState(null);
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {mode, data}
  const [saving, setSaving] = useState(false);
  const [nudges, setNudges] = useState([]);

  const load = () => {
    setLoading(true);
    Promise.allSettled([
      api.get("/providers/me/earnings"),
      api.get("/providers/me/listings"),
      api.get("/providers/me/bookings"),
      api.get("/providers/me/bonus-nudges"),
    ])
      .then(([e, l, b, n]) => {
        if (e.status === "fulfilled") setEarnings(e.value.data);
        if (l.status === "fulfilled") setListings(l.value.data);
        if (b.status === "fulfilled") setBookings(b.value.data);
        if (n.status === "fulfilled") setNudges(n.value.data.nudges || []);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { refreshUser(); }, []); // eslint-disable-line

  const nudgeFor = (id) => nudges.find((n) => n.tour_id === id);
  const boostTour = (l) => {
    const n = nudgeFor(l.id);
    openEdit({ ...l, affiliate_enabled: true, affiliate_bonus_enabled: true, affiliate_bonus_rate: (n?.suggested_bonus || 0.05) });
  };

  const openCreate = () => setModal({ mode: "create", data: { ...EMPTY } });
  const openEdit = (l) => setModal({
    mode: "edit", id: l.id,
    data: {
      ...l, images: l.images?.length ? l.images : [""],
      amenities: (l.amenities || []).join(", "),
      affiliate_enabled: !!l.affiliate_enabled,
      affiliate_bonus_enabled: !!l.affiliate_bonus_enabled,
      affiliate_bonus_rate: l.affiliate_bonus_enabled ? Math.round((l.affiliate_bonus_rate || 0) * 100) : (l.affiliate_bonus_rate ? Math.round(l.affiliate_bonus_rate * 100) : 5),
    },
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const d = modal.data;
    const payload = {
      title: d.title, description: d.description, island: d.island, category: d.category,
      price: Number(d.price), price_type: d.price_type || "per_person",
      duration: d.duration, meeting_point: d.meeting_point,
      max_guests: Number(d.max_guests),
      location: d.location, images: d.images.filter(Boolean),
      amenities: typeof d.amenities === "string" ? d.amenities.split(",").map((a) => a.trim()).filter(Boolean) : d.amenities,
      is_available: d.is_available,
      cancellation_window: Number(d.cancellation_window) === 48 ? 48 : 24,
      affiliate_enabled: !!d.affiliate_enabled,
      affiliate_bonus_enabled: !!(d.affiliate_enabled && d.affiliate_bonus_enabled),
      affiliate_bonus_rate: (d.affiliate_enabled && d.affiliate_bonus_enabled) ? Math.max(0, Number(d.affiliate_bonus_rate) || 0) / 100 : 0,
    };
    if (!payload.images.length) payload.images = ["https://images.unsplash.com/photo-1784730339452-fa650e0aaab4?crop=entropy&cs=srgb&fm=jpg&q=85"];
    try {
      if (modal.mode === "create") await api.post("/listings", payload);
      else await api.put(`/listings/${modal.id}`, payload);
      toast.success(modal.mode === "create" ? "Listing published!" : "Listing updated!");
      setModal(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/listings/${id}`); toast.success("Listing removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleAvail = async (l) => {
    try {
      await api.put(`/listings/${l.id}`, {
        title: l.title, description: l.description, island: l.island, category: l.category,
        price: l.price, price_type: l.price_type, duration: l.duration, meeting_point: l.meeting_point,
        max_guests: l.max_guests, location: l.location,
        images: l.images, amenities: l.amenities, is_available: !l.is_available, cancellation_window: l.cancellation_window || 24,
        affiliate_enabled: !!l.affiliate_enabled, affiliate_bonus_enabled: !!l.affiliate_bonus_enabled, affiliate_bonus_rate: l.affiliate_bonus_rate || 0,
      });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setField = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));
  const [chat, setChat] = useState(null);
  const [memBooking, setMemBooking] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const c = searchParams.get("chat");
    if (c) {
      setChat(c);
      searchParams.delete("chat");
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line

  const requestPayout = async (id) => {
    try {
      const { data } = await api.post(`/bookings/${id}/request-payout`);
      toast.success(data.message || "Payout requested");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#060810]">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-10 pb-16">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <span className="font-mono-caption text-xs uppercase tracking-[0.2em] text-violet-400">Host Studio</span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white mt-1">{user?.name}</h1>
          </div>
          <button onClick={openCreate} data-testid="new-listing-btn" className="btn-neon rounded-full px-6 py-2.5 font-semibold flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> New listing
          </button>
        </div>

        {/* Earnings */}
        {earnings && (
          <div data-testid="commission-breakdown-card" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-10">
            <StatCard icon={TrendingUp} color="cyan" label="Gross earnings" value={`$${earnings.gross_earnings.toLocaleString()}`} sub={`${earnings.total_bookings} paid bookings`} testid="provider-earnings-gross" />
            <StatCard icon={Percent} color="coral" label="Platform fee (20%)" value={`-$${earnings.commission_fee.toLocaleString()}`} sub="Carivisor commission" testid="provider-earnings-commission" />
            <StatCard icon={Wallet} color="emerald" label="Net payout" value={`$${earnings.net_payout.toLocaleString()}`} sub={`Available now: $${(earnings.available_payout ?? 0).toLocaleString()} · Pending: $${(earnings.pending_payout ?? 0).toLocaleString()}`} testid="provider-earnings-net" highlight />
            <StatCard icon={LayoutList} color="violet" label="Active listings" value={listings.filter((l) => l.is_available).length} sub={`${listings.length} total`} testid="provider-active-listings" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#1D2640]">
          {[
            { key: "overview", label: "My Listings", icon: LayoutList },
            { key: "bookings", label: "Bookings", icon: Receipt },
            { key: "cars", label: "Car Rentals", icon: Car },
          ].map((t) => (
            <button key={t.key} data-testid={`provider-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-cyan-400 text-cyan-400" : "border-transparent text-slate-400 hover:text-white"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
        ) : tab === "overview" ? (
          listings.length === 0 ? (
            <div className="text-center py-24 glass rounded-2xl">
              <p className="font-heading text-xl text-white mb-2">No listings yet</p>
              <p className="text-slate-400 text-sm mb-6">Publish your first villa, charter, or experience.</p>
              <button onClick={openCreate} className="btn-neon rounded-full px-6 py-2.5 font-semibold text-sm">Create a listing</button>
            </div>
          ) : (
            <div className="space-y-4">
              {nudges.length > 0 && (
                <div data-testid="bonus-nudge-banner" className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-r from-fuchsia-500/10 to-cyan-500/10 p-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-grad-warm flex items-center justify-center text-white shrink-0"><Sparkles className="w-5 h-5" /></div>
                  <div className="flex-1">
                    <p className="font-heading font-semibold text-white">{nudges.length === 1 ? "1 tour is getting agent clicks but few bookings" : `${nudges.length} tours are getting agent clicks but few bookings`}</p>
                    <p className="text-slate-300 text-sm mt-0.5">Agents are sending traffic to {nudges.length === 1 ? "this tour" : "these tours"}, but visitors aren't converting. Add a <span className="text-fuchsia-300 font-semibold">Bonus Boost</span> to motivate agents to close more sales.</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {nudges.map((n) => (
                        <button key={n.tour_id} data-testid={`nudge-boost-${n.tour_id}`}
                          onClick={() => { const l = listings.find((x) => x.id === n.tour_id); if (l) boostTour(l); }}
                          className="text-xs font-semibold px-3 py-1.5 rounded-full bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-400/30 hover:bg-fuchsia-500/30 flex items-center gap-1.5">
                          <Zap className="w-3.5 h-3.5" /> Boost "{n.title.length > 24 ? n.title.slice(0, 24) + "…" : n.title}" ({n.clicks} clicks · {n.sales} sales)
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {listings.map((l) => {
                const nudge = nudgeFor(l.id);
                return (
                <div key={l.id} data-testid={`provider-listing-${l.id}`} className={`glass rounded-2xl p-4 flex flex-col sm:flex-row gap-4 items-start ${nudge ? "ring-1 ring-fuchsia-400/40" : ""}`}>
                  <img src={l.images?.[0]} alt="" className="w-full sm:w-32 h-24 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading font-semibold text-white">{l.title}</h3>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${l.is_available ? "text-emerald-400 border-emerald-400/30" : "text-slate-500 border-slate-600"}`}>
                        {l.is_available ? "Live" : "Hidden"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">{l.island} · {l.category}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="font-heading font-bold text-white">${l.price?.toLocaleString()}<span className="text-xs text-slate-500 font-normal"> {l.price_type === "flat" ? "flat" : "pp"}</span></span>
                      <span className="flex items-center gap-1 text-slate-400 text-xs"><Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> {l.rating?.toFixed(1)}</span>
                    </div>
                    {nudge && (
                      <div data-testid={`nudge-inline-${l.id}`} className="mt-3 rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <span className="flex items-center gap-1.5 text-xs text-fuchsia-200"><Sparkles className="w-3.5 h-3.5 text-fuchsia-300" /> {nudge.clicks} agent clicks · only {nudge.sales} sale{nudge.sales === 1 ? "" : "s"} — add a bonus to convert more.</span>
                        <button onClick={() => boostTour(l)} data-testid={`nudge-boost-inline-${l.id}`}
                          className="text-xs font-semibold px-3 py-1 rounded-full btn-neon flex items-center gap-1">
                          <Zap className="w-3.5 h-3.5" /> Add {Math.round((nudge.suggested_bonus || 0.05) * 100)}% Bonus Boost
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => toggleAvail(l)} data-testid={`toggle-avail-${l.id}`} title="Toggle availability"
                      className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-300 hover:text-cyan-400">
                      {l.is_available ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button onClick={() => openEdit(l)} data-testid={`edit-listing-${l.id}`}
                      className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-300 hover:text-cyan-400">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => del(l.id)} data-testid={`delete-listing-${l.id}`}
                      className="w-9 h-9 rounded-lg glass flex items-center justify-center text-slate-300 hover:text-[#FF5E7E]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          )
        ) : tab === "cars" ? (
          <CarProviderPanel />
        ) : bookings.length === 0 ? (
          <div className="text-center py-24 glass rounded-2xl">
            <p className="font-heading text-xl text-white mb-2">No bookings yet</p>
            <p className="text-slate-400 text-sm">Bookings from travelers will appear here with your 80% payout.</p>
          </div>
        ) : (
          <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-[#1D2640]">
                  <th className="px-5 py-4 font-medium">Guest</th>
                  <th className="px-5 py-4 font-medium">Listing</th>
                  <th className="px-5 py-4 font-medium">Dates</th>
                  <th className="px-5 py-4 font-medium text-right">Gross</th>
                  <th className="px-5 py-4 font-medium text-right">Fee (20%)</th>
                  <th className="px-5 py-4 font-medium text-right">Net</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                  <th className="px-5 py-4 font-medium">Payout</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`provider-booking-${b.id}`} className="border-b border-[#1D2640]/50 hover:bg-[#0B0E1B]/50">
                    <td className="px-5 py-4 text-white">
                      <div className="flex items-center gap-2">
                        {b.traveler_name}
                        <button onClick={() => setChat(b.id)} data-testid={`provider-chat-${b.id}`} className="text-fuchsia-400 hover:text-fuchsia-300" title="Open trip chat"><MessageSquare className="w-4 h-4" /></button>
                        <button onClick={() => setMemBooking(b)} data-testid={`provider-memories-${b.id}`} className="text-cyan-400 hover:text-cyan-300 relative" title="Add trip memories">
                          <Images className="w-4 h-4" />
                          {b.memories?.length > 0 && <span className="absolute -top-1.5 -right-1.5 text-[8px] bg-cyan-500 text-[#060810] rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">{b.memories.length}</span>}
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate-300">{b.listing_title}</td>
                    <td className="px-5 py-4 text-slate-400 text-xs">{b.date} · {b.time}</td>
                    <td className="px-5 py-4 text-right text-white">${b.gross_amount?.toLocaleString()}</td>
                    <td className="px-5 py-4 text-right text-[#FF5E7E]">-${b.commission?.toLocaleString()}</td>
                    <td className="px-5 py-4 text-right text-emerald-400 font-semibold">${b.net_payout?.toLocaleString()}</td>
                    <td className="px-5 py-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${
                        b.payment_status === "paid" ? "text-emerald-400 border-emerald-400/30" : "text-amber-400 border-amber-400/30"
                      }`}>{b.payment_status === "paid" ? "Paid" : "Pending"}</span>
                    </td>
                    <td className="px-5 py-4">
                      {b.status === "cancelled" ? (
                        <span className="text-xs text-slate-500">—</span>
                      ) : b.payout_status === "requested" ? (
                        <span className="text-xs text-cyan-300" data-testid={`payout-requested-${b.id}`}>Requested</span>
                      ) : b.payout_status === "paid" ? (
                        <span className="text-xs text-emerald-400">Paid out</span>
                      ) : b.payout_available ? (
                        <button data-testid={`request-payout-${b.id}`} onClick={() => requestPayout(b.id)}
                          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25">
                          Request payout
                        </button>
                      ) : b.payment_status === "paid" ? (
                        <span className="text-[11px] text-amber-400/80 flex items-center gap-1" title={`Available ${b.payout_available_at || ""}`} data-testid={`payout-pending-${b.id}`}>
                          <Clock className="w-3 h-3" /> Pending 10d
                        </span>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setModal(null)}>
          <div className="glass neon-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="listing-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-xl font-bold text-white">{modal.mode === "create" ? "New listing" : "Edit listing"}</h2>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <Field label="Title"><input required data-testid="listing-title-input" value={modal.data.title} onChange={(e) => setField("title", e.target.value)} className={inputCls} placeholder="St. George's Spice Tour" /></Field>
              <Field label="Description"><textarea required rows={3} data-testid="listing-desc-input" value={modal.data.description} onChange={(e) => setField("description", e.target.value)} className={inputCls} placeholder="Describe the excursion…" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Island">
                  <select value={modal.data.island} onChange={(e) => setField("island", e.target.value)} className={inputCls}>
                    {ISLANDS.map((i) => <option key={i} value={i} className="bg-[#0B0E1B]">{i}</option>)}
                  </select>
                </Field>
                <Field label="Category">
                  <select value={modal.data.category} onChange={(e) => setField("category", e.target.value)} className={inputCls}>
                    {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#0B0E1B]">{c}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Price ($)"><input type="number" required min={1} data-testid="listing-price-input" value={modal.data.price} onChange={(e) => setField("price", e.target.value)} className={inputCls} /></Field>
                <Field label="Pricing">
                  <select data-testid="listing-pricetype-input" value={modal.data.price_type} onChange={(e) => setField("price_type", e.target.value)} className={inputCls}>
                    <option value="per_person" className="bg-[#0B0E1B]">Per person</option>
                    <option value="flat" className="bg-[#0B0E1B]">Flat rate</option>
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Duration"><input data-testid="listing-duration-input" value={modal.data.duration} onChange={(e) => setField("duration", e.target.value)} className={inputCls} placeholder="4 hours" /></Field>
                <Field label="Max travelers"><input type="number" required min={1} value={modal.data.max_guests} onChange={(e) => setField("max_guests", e.target.value)} className={inputCls} /></Field>
              </div>
              <Field label="Free cancellation window">
                <select data-testid="listing-cancellation-input" value={modal.data.cancellation_window} onChange={(e) => setField("cancellation_window", Number(e.target.value))} className={inputCls}>
                  <option value={24} className="bg-[#0B0E1B]">24 hours before — full refund</option>
                  <option value={48} className="bg-[#0B0E1B]">48 hours before — full refund</option>
                </select>
              </Field>
              <Field label="Meeting point"><input data-testid="listing-meeting-input" value={modal.data.meeting_point} onChange={(e) => setField("meeting_point", e.target.value)} className={inputCls} placeholder="Carenage Waterfront, St. George's" /></Field>
              <Field label="Location (island area)"><input value={modal.data.location} onChange={(e) => setField("location", e.target.value)} className={inputCls} placeholder="St. George's, Grenada" /></Field>
              <Field label="Image URL"><input value={modal.data.images[0]} onChange={(e) => setField("images", [e.target.value])} className={inputCls} placeholder="https://…" /></Field>
              <Field label="What's included (comma separated)"><input value={modal.data.amenities} onChange={(e) => setField("amenities", e.target.value)} className={inputCls} placeholder="Guide, Hotel Pickup, Tastings" /></Field>

              {/* Marketplace Affiliate Opt-In */}
              <div className="rounded-xl border border-[#1D2640] bg-[#0B0E1B] p-4 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="affiliate-enable-label">
                  <input type="checkbox" data-testid="affiliate-enabled-input" checked={!!modal.data.affiliate_enabled}
                    onChange={(e) => setField("affiliate_enabled", e.target.checked)}
                    className="mt-1 w-4 h-4 accent-cyan-500" />
                  <span>
                    <span className="font-heading font-semibold text-white text-sm block">Allow agents to promote this tour for a bonus?</span>
                    <span className="text-xs text-slate-400">Agents share tracking links and earn a commission (from your net payout) on each sale they bring.</span>
                  </span>
                </label>
                {modal.data.affiliate_enabled && (
                  <div className="pl-7 space-y-3 border-t border-[#1D2640] pt-3">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="affiliate-bonus-label">
                      <input type="checkbox" data-testid="affiliate-bonus-input" checked={!!modal.data.affiliate_bonus_enabled}
                        onChange={(e) => setField("affiliate_bonus_enabled", e.target.checked)}
                        className="mt-1 w-4 h-4 accent-fuchsia-500" />
                      <span>
                        <span className="font-heading font-semibold text-white text-sm block">Offer a "Bonus Boost"</span>
                        <span className="text-xs text-slate-400">Add extra commission on top of the platform base rate to encourage more sales.</span>
                      </span>
                    </label>
                    {modal.data.affiliate_bonus_enabled && (
                      <Field label="Bonus commission (%)">
                        <input type="number" min={0} max={50} step={0.5} data-testid="affiliate-bonus-rate-input"
                          value={modal.data.affiliate_bonus_rate}
                          onChange={(e) => setField("affiliate_bonus_rate", e.target.value)}
                          className={inputCls} placeholder="5" />
                      </Field>
                    )}
                  </div>
                )}
              </div>

              <button disabled={saving} data-testid="save-listing-btn" className="w-full btn-neon rounded-xl py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : modal.mode === "create" ? "Publish listing" : "Save changes"}
              </button>
            </form>
          </div>
        </div>
      )}
      {chat && <TripChat bookingId={chat} onClose={() => setChat(null)} />}
      {memBooking && <MemoriesModal booking={memBooking} onClose={() => setMemBooking(null)} onDone={() => { setMemBooking(null); load(); }} />}
      <Footer />
    </div>
  );
}

function MemoriesModal({ booking, onClose, onDone }) {
  const [urls, setUrls] = useState([]);
  const [saving, setSaving] = useState(false);
  const existing = booking.memories?.length || 0;

  const save = async () => {
    if (!urls.length) { toast.error("Add at least one photo"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/memories`, { urls });
      toast.success(`${data.added} memory photo(s) shared with ${booking.traveler_name}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={onClose}>
      <div className="glass neon-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="memories-modal">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2"><Images className="w-5 h-5 text-cyan-400" /> Trip memories</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-slate-400 text-sm mb-1">{booking.listing_title} · {booking.traveler_name}</p>
        <p className="text-xs text-slate-500 mb-5">
          {existing > 0 ? `${existing} photo(s) already shared. ` : ""}
          Travelers see a watermarked preview and unlock the full-resolution photos for $1 — you keep 80%.
        </p>
        <MultiImageUpload value={urls} onChange={setUrls} max={12} testid="memories-upload" />
        <button onClick={save} disabled={saving || !urls.length} data-testid="memories-save-btn"
          className="w-full btn-neon rounded-xl py-3 font-semibold flex items-center justify-center gap-2 mt-5 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Images className="w-4 h-4" />} Share {urls.length || ""} memories
        </button>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-slate-400 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

const COLORS = {
  cyan: "text-cyan-400", emerald: "text-emerald-400", violet: "text-violet-400", coral: "text-[#FF5E7E]",
};
function StatCard({ icon: Icon, color, label, value, sub, testid, highlight }) {
  return (
    <div data-testid={testid} className={`glass rounded-2xl p-5 ${highlight ? "neon-border" : ""}`}>
      <div className={`w-10 h-10 rounded-xl bg-[#0B0E1B] border border-[#1D2640] flex items-center justify-center mb-4 ${COLORS[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className={`font-heading text-2xl font-bold mt-1 ${highlight ? "neon-text-cyan" : "text-white"}`}>{value}</p>
      <p className="text-xs text-slate-500 mt-1">{sub}</p>
    </div>
  );
}
